import type { AfterViewInit, OnDestroy, TemplateRef } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    HostListener,
    ViewChild,
    ViewContainerRef,
    computed,
    inject,
    input,
    output,
} from '@angular/core';

import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { Store } from '@ngrx/store';

import type { FormInputSettingsData } from '@sites/entity-core';
import type { LoginEduForm } from '@sites/entity-signal-form';

import { ELoginEduView } from '@common/enums/login-edu-view-form.enum';
import { previewPortalAttachAction } from '@web/store/page-structure/widgets/widgets.actions';

import type { LoginEduFormViewData } from '@sites/entity-core/src/widget/login-edu/styles/login-edu.css-vars';
import {
    getFormAlignCssVars,
    getFormInputCssVars,
    getFormSizeCssVars,
} from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import {
    getLoginEduButtonWrapperCssVars,
    getLoginEduFormViewClasses,
    getLoginEduLinkCssVars,
    getLoginEduModalCssVars,
    getLoginEduPasswordIconCssVars,
} from '@sites/entity-core/src/widget/login-edu/styles/login-edu.css-vars';

import { SpFormInputSettingsSignalComponent } from '@components/sp-form-input-settings-signal';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

import { EduLoginModalComponent } from './edu-login-form-modal/edu-login-modal-preview.component';
import { colorCss } from '@web/widgets/common/css-vars/css-vars.utils';

/**
 * Field-appearance panel. Value-in/event-out over the widget signal form's
 * `inputSettings` group. When the widget renders as a modal, the panel also
 * teleports a live modal preview into the editor's `#modalPreviewContent`
 * outlet (a DOM portal — the preview must render outside this panel's subtree).
 */
@Component({
    selector: 'web-form-input-settings',
    templateUrl: './field-input-settings.component.html',
    styleUrls: ['./field-input-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EduLoginModalComponent, CommonElementsModule, SpFormInputSettingsSignalComponent],
})
export class FieldInputSettingsComponent implements AfterViewInit, OnDestroy {
    public readonly form = input.required<LoginEduForm>();

    public readonly saveEvent = output<void>();
    public readonly cancelEvent = output<void>();

    @ViewChild('modalPreviewPortal') public modalPreviewPortal: TemplateRef<unknown>;

    private readonly store$: Store = inject(Store);
    private readonly document: Document = inject(DOCUMENT);
    private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
    private portalHost: DomPortalOutlet;

    public readonly settings = computed<FormInputSettingsData | null>(() => this.form().inputSettings().value());

    /** Drives the lib's modal-only blocks — the login-edu branch of the original. */
    public readonly isEduModalWidget = computed<boolean>(() => this.form().formView().value() === ELoginEduView.modal);

    /**
     * Styling contract handed to the live modal preview. Built here rather than
     * in the preview so the preview stays presentational, and so it matches
     * exactly what the published modal renders (`login-edu.component.ts` builds
     * the same bag for its modals).
     */
    public readonly previewStyleBag = computed<LoginEduFormViewData>(() => {
        const inputSettings = this.settings() as NonNullable<FormInputSettingsData>;

        return {
            ...getLoginEduFormViewClasses(inputSettings as never),
            rootVars: {
                ...getFormInputCssVars(inputSettings as never, colorCss),
                ...getFormSizeCssVars(inputSettings as never),
                ...getFormAlignCssVars(inputSettings as never),
                ...getLoginEduLinkCssVars(inputSettings as never, colorCss),
                ...getLoginEduPasswordIconCssVars(inputSettings as never, colorCss),
                ...getLoginEduModalCssVars(inputSettings as never, colorCss),
            },
            wrapperVars: getLoginEduButtonWrapperCssVars(inputSettings?.inputSize as never),
            buttonVars: {},
            buttonClasses: '',
            buttonLabel: '',
            elements: [],
        };
    });

    public onSettingsChange(value: FormInputSettingsData): void {
        this.form().setInputSettings(value);
    }

    public ngAfterViewInit(): void {
        if (!this.isEduModalWidget()) return;

        const portalHTMLElement = this.document.querySelector('#modalPreviewContent');
        if (!portalHTMLElement) return;

        this.portalHost = new DomPortalOutlet(portalHTMLElement);
        this.portalHost.attach(new TemplatePortal(this.modalPreviewPortal, this.viewContainerRef));
        this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: true }));
    }

    public ngOnDestroy(): void {
        if (!this.portalHost) return;

        this.portalHost.detach();
        this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: false }));
    }

    public onSave(): void {
        this.saveEvent.emit();
    }

    public onReset(): void {
        this.cancelEvent.emit();
    }

    @HostListener('document:keydown.enter')
    public onEnterKeyPress(): void {
        this.onSave();
    }

    @HostListener('document:keydown.escape', ['$event'])
    public onEscapeKeyPress(event: KeyboardEvent): void {
        event.stopPropagation();
        this.onReset();
    }
}
